import React from "react";
import type { NegotiationBalance, NegotiationTopupOrigin } from "../../services/negotiationBalance";
import { isResumableTopup, rechargePrefillReais } from "./negotiationBalanceFormat";
import { RechargeModal } from "./RechargeModal";
import { useTermsAcceptance } from "./TermsAcceptanceFlow";
import { TermsAcceptanceModal } from "./TermsAcceptanceModal";

export type RechargeFlowProps = {
  origin: NegotiationTopupOrigin;
  /**
   * Prefill in whole reais (the shortfall of a bid, rounded up). It is raised
   * to the minimum top-up and capped at the maximum of `balance.limits`.
   */
  initialAmountBRL?: number;
  /**
   * Open this existing top-up (a QR left open earlier) instead of the amount
   * form. Its QR already exists, so the conditions are not asked again.
   */
  resumeTopupId?: string;
  /** The last balance answer; its terms, limits and top-up fee drive the flow. */
  balance: NegotiationBalance;
  /** `org_payments` create: accepting the conditions is a payments manager's act. */
  canAcceptTerms: boolean;
  /** The balance may have moved (terms accepted, QR paid): read it again. */
  onBalanceChanged: () => void;
  onClose: (outcome: { credited: boolean }) => void;
};

/**
 * Recharge from the payments tab or from inside the auction. When the
 * company has not accepted the current conditions yet (the answer says so,
 * or Payments refuses the top-up with negotiation_terms_required), the
 * acceptance comes first and the recharge follows with the same amount. The
 * company's QRs still open (listed by the balance answer) can be shown again
 * from the amount form, to pay or cancel them.
 * @story skip request lifecycle is covered by CI unit tests; see TermsAcceptanceModal and RechargeModalView for every visual state
 */
export function RechargeFlow({
  origin,
  initialAmountBRL,
  resumeTopupId,
  balance,
  canAcceptTerms,
  onBalanceChanged,
  onClose,
}: RechargeFlowProps) {
  const [step, setStep] = React.useState<"terms" | "recharge">(
    balance.terms.required && !resumeTopupId ? "terms" : "recharge",
  );
  // Every "Ver QR" mounts a fresh modal (seq), even for the QR shown last.
  const [resume, setResume] = React.useState<{ id?: string; seq: number }>({
    id: resumeTopupId,
    seq: 0,
  });
  // The amount survives the detour through the acceptance: the terms step
  // unmounts the recharge modal, and the next one starts from this.
  const [amountBRL, setAmountBRL] = React.useState(() =>
    rechargePrefillReais(initialAmountBRL, balance.limits),
  );
  const terms = useTermsAcceptance({
    version: balance.terms.version,
    canAccept: canAcceptTerms,
    onBalanceChanged,
    onAccepted: () => setStep("recharge"),
  });

  if (step === "terms") {
    return (
      <TermsAcceptanceModal
        version={balance.terms.version}
        published={terms.published}
        canAccept={canAcceptTerms}
        accepting={terms.accepting}
        error={terms.error}
        onAccept={terms.accept}
        onClose={() => onClose({ credited: false })}
      />
    );
  }
  return (
    <RechargeModal
      // A resumed QR is its own flow: a fresh modal polls it from the start.
      key={resume.seq}
      origin={origin}
      initialAmountBRL={amountBRL}
      resumeTopupId={resume.id}
      openTopups={balance.pendingTopups.filter(isResumableTopup)}
      onResumeTopup={(id) => setResume((previous) => ({ id, seq: previous.seq + 1 }))}
      onTopupLimit={onBalanceChanged}
      limits={balance.limits}
      topupFee={balance.topupFee}
      topupsEnabled={balance.topupsEnabled}
      onTermsRequired={(typed) => {
        setAmountBRL(typed);
        setResume((previous) => ({ id: undefined, seq: previous.seq }));
        setStep("terms");
      }}
      onClose={onClose}
    />
  );
}
